import _ from "lodash";
import { apiClient, getCachedClient, handleNetworkResponse } from "utils/clients";
import externals from "utils/externals";

const userPreferencesAPIUrl = externals.stargateV4BackendGraphQlUrl;

const USER_PREFERENCE_DEBOUNCED_DELAY = 2000;

const parsePreference = pref => ({
    ...pref,
    data: pref?.data ? JSON.parse(pref.data) : null
});

const createPreference = async ({ name, data }) => {
    const response = await apiClient.post(userPreferencesAPIUrl, {
        query: `
                mutation CreateUserPreference($name: String!, $data: String!) {
                    createUserPreference(name: $name, data: $data) {
                        id
                        name
                        data
                    }
                }
            `,
        variables: {
            name,
            data: JSON.stringify(data)
        }
    });
    if (response.data.errors) {
        throw new Error(response.data.errors[0].message);
    }
    return parsePreference(response.data.data.createUserPreference);

};

const updatePreference = async ({ id, data }) => {
    const response = await apiClient.post(userPreferencesAPIUrl, {
        query: `
                mutation UpdateUserPreference($id: String!, $data: String!) {
                    updateUserPreference(id: $id, data: $data) {
                        id
                        data
                    }
                }
            `,
        variables: {
            id,
            data: JSON.stringify(data)
        }
    });
    if (response.data.errors) {
        throw new Error(response.data.errors[0].message);
    }
    return parsePreference(response.data.data.updateUserPreference);
};

const fetchPreference = async ({ name }, onNetworkSuccess, onNetworkError, args) => {
    const {response, networkResponse} = await getCachedClient(apiClient, userPreferencesAPIUrl, `fetch-user-preference-${name}`, { isGQL: true, body: {
        query: `
                query UserPreferences {
                    userPreferences(filter: { name: "${name}" }) {
                        userPreferences {
                            id
                            name
                            data
                        }
                    }
                }
            `
    }}, false, "stale_while_revalidate");
    handleNetworkResponse(networkResponse, (response, args) => {
        try {
                const fetchedData = handleFetchResponse(response, name);
                onNetworkSuccess(fetchedData, args);
            } catch (error) {
                onNetworkError(error, args);
            }
        }, (error, args) => {  
            onNetworkError(error, args);
        }, args);
    return handleFetchResponse(response, name);
};

const handleFetchResponse = (response, name) => {
    if (response.data.errors) {
        throw new Error(response.data.errors[0].message);
    }
    if (_.isEmpty(response.data.data?.userPreferences?.userPreferences)) {
        return { id: null, name, data: null };
    }
    return parsePreference(response.data.data.userPreferences.userPreferences[0]);
}

export default ({ dispatch, getState }) => next => async action => {
    const { type, payload } = action;
    if (type === 'PREFERENCES:FETCH') {
        const { name } = payload;
        dispatch({ type: 'PREFERENCES:FETCH_START', payload: { name } });
        try {
            const fetchedData = await fetchPreference({ name },
                (fetchedData, { dispatch }) => {
                    dispatch({ type: 'PREFERENCES:FETCH_SUCCESS', payload: fetchedData });
                },
                (error, { name, dispatch }) => {
                    dispatch({ type: 'PREFERENCES:FETCH_ERROR', payload: { name, error } });
                },
                { name, dispatch }
            );
            dispatch({ type: 'PREFERENCES:FETCH_SUCCESS', payload: fetchedData });
        } catch (error) {
            dispatch({ type: 'PREFERENCES:FETCH_ERROR', payload: { name, error } });
        }
    }
    else if (type === 'PREFERENCES:SAVE_OR_UPDATE') {
        const { name, data } = payload;
        try {
            dispatch({ type: 'PREFERENCES:SAVE_OR_UPDATE_LOCAL', payload: { name, data } });
            const prefState = getState().userPreferences[name];
            if (prefState?.initialLoaded) {
                _.debounce((id, name, data) => {
                    dispatch({ type: 'PREFERENCES:SAVE_OR_UPDATE_REMOTE', payload: { id, name, data } });
                }, USER_PREFERENCE_DEBOUNCED_DELAY)(prefState.id, name, data);
            }
        } catch (error) {
            console.error('Error saving/updating user preference:', error);
        }
    } else if (type === 'PREFERENCES:SAVE_OR_UPDATE_REMOTE') {
        const { id, name, data } = payload;
        try {
            let pref;
            if (id) {
                pref = await updatePreference({ dispatch, id, data });
            } else {
                pref = await createPreference({ dispatch, name, data });
            }
            dispatch({ type: 'PREFERENCES:SAVE_OR_UPDATE_SUCCESS', payload: { id, name, ...pref} });
        } catch (error) {
            console.error('Error saving/updating user preference', error);
        }
    }

    return next(action);
};

export const PREFERENCES_KEYS = {
    APP_PREFERENCES: 'app_preferences',
    SPLASH_SCREEN_DISMISSED_PREF_KEY: "splashscreen_v1_dismissed",
    HOME_PAGE_PREFERENCES: 'home_page_preferences',
    HOMEPAGE_PREFERENCE_DASHBOARD: 'homepage_preference_dashboard'
}